<template>
  <div class="h3">Категории номеров</div>
  <div class="paragraph-m text-secondary mt-4">Выберите номера, которые будут продаваться по тарифу</div>
  <div v-if="activeTariff?.baseSettings?.completed" class="text-secondary paragraph-m_h">
    <div class="d-flex mt-4">
      <e-icon name="lock" class="mr-2" />

      <p>—&nbsp;&nbsp;категории номеров, по которым есть продажи</p>
    </div>
    <div class="d-flex mt-4">
      <div class="with-badge mr-2 mt-1">&nbsp;</div>

      <p>
        &nbsp;&nbsp;—&nbsp;&nbsp;категории номеров, по которым нет продаж, но уже есть настройки квоты, после снятия
        выбора с такого номера эти <span class="text-red">настройки удаляются</span>
      </p>
    </div>
  </div>
  <e-form class="d-flex flex-column align-center mt-10">
    <div data-wide-content>
      <e-alert v-model="isError" variant="error" closable thin class="mb-10">
        <div class="paragraph-m">Не выбрана ни одна категория номера</div>
      </e-alert>

      <e-data-items
        v-if="roomCategories?.roomCategories.data || isLoading"
        v-model="selected"
        item-value="cid"
        :headers="headers"
        :items="roomCategories.roomCategories.data || []"
        :loading="isLoading"
      >
        <template v-slot:[`header.data-table-select`]="props">
          <e-checkbox
            :model-value="props.allSelected"
            :square-selected="props.someSelected"
            @update:modelValue="onSelectAllClick"
          />
        </template>
        <template v-slot:[`item.data-table-select`]="{ isSelected, internalItem, toggleSelect, item }">
          <e-tooltip v-if="roomQuotaStatus(item.cid) === QuotaStatus.QuotaInWork" :max-width="364">
            <template #activator>
              <e-icon name="lock" class="text-secondary d-block" />
            </template>
            <div class="text-secondary">
              <div class="paragraph-s font-600 mb-2">Квота в работе</div>
              <div class="paragraph-xs">Исключение категорий номера из Вариантов размещения запрещается</div>
            </div>
          </e-tooltip>
          <e-checkbox
            v-else
            :model-value="isSelected(internalItem)"
            @update:modelValue="toggleSelect(internalItem, $event)"
          />
        </template>
        <template v-slot:[`item.name`]="{ item }">
          <span :class="{ 'with-badge': roomQuotaStatus(item.cid) === QuotaStatus.QuotaIsDraft }">
            {{ item.name[0].value }}
          </span>
        </template>
      </e-data-items>
    </div>

    <div class="d-flex justify-end mt-10 w-100">
      <e-btn append-icon-src="arrowRight" variant="primary" @click="next"> Далее</e-btn>
    </div>
  </e-form>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { DataTableHeaders } from '@/core/types/components/table';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import { EAlert, EBtn, ECheckbox, EForm, EIcon, ETooltip } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { QuotaStatus } from '@/graphql/generated';
import { useRoomCategoriesStore } from '@/parts/room-categories/store/roomCategories';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const props = defineProps<{
  selectedRoomCategories: { roomCategoryCid: string; quotaStatus: QuotaStatus }[];
}>();

const { getRoomCategories, roomCategories } = useRoomCategoriesStore();
const { activeTariff } = useTariffsStore();

const emit = defineEmits<{
  (event: 'next'): void;
}>();

const isError = ref(false);

const selected = defineModel<string[]>({ required: true });

const next = () => {
  isError.value = !selected.value?.length;

  if (!selected.value?.length) return;

  emit('next');
};

const onSelectAllClick = (value: boolean) => {
  const items = roomCategories.value?.roomCategories.data || [];

  if (value) {
    selected.value = items.map((item) => item.cid);
  } else {
    const lockedIds = props.selectedRoomCategories
      .filter((rc) => rc.quotaStatus === QuotaStatus.QuotaInWork)
      .map((rc) => rc.roomCategoryCid);

    selected.value = lockedIds;
  }
};

const roomQuotaStatus = (roomCategoryCid: string) => {
  const selectedRoomCategory = props.selectedRoomCategories.find((rc) => rc.roomCategoryCid === roomCategoryCid);

  if (!selectedRoomCategory) {
    return null;
  }

  return selectedRoomCategory.quotaStatus;
};

const headers: DataTableHeaders = [
  {
    key: 'name',
    title: 'Категория номера',
  },
  {
    key: 'type.name',
    title: 'Тип',
    align: 'start',
    width: 250,
    sortable: false,
  },
  {
    key: 'seat.baseQuantity',
    title: 'Осн',
    align: 'start',
    sortable: false,
    width: 80,
  },
  {
    key: 'seat.extQuantity',
    title: 'Доп.',
    align: 'start',
    sortable: false,
    width: 80,
  },
];

const { isLoading } = useFetch(async () => {
  await getRoomCategories();
});
</script>

<style scoped lang="scss"></style>
